import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import type { EntityDetail } from "../../shared/api";
import { dimensionsFor } from "../../shared/dimensions";
import { Spinner, ErrorState } from "../components/States";
import { Stars } from "../components/Stars";
import { VerificationBadge } from "../components/Badge";
import { metaFor } from "../../shared/seo";
import { useMeta } from "../lib/seo";
import { origin } from "../lib/origin";

interface Eth {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
}
declare global {
  interface Window { ethereum?: Eth; }
}

export function ReviewNew() {
  const { entityId } = useParams();
  const navigate = useNavigate();
  const [e, setE] = useState<EntityDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [rating, setRating] = useState(0);
  const [dims, setDims] = useState<Record<string, number>>({});
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [verifToken, setVerifToken] = useState<string | null>(null);
  const [verifBadge, setVerifBadge] = useState<string | null>(null);
  const [verifBusy, setVerifBusy] = useState(false);
  const [verifErr, setVerifErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<{ verificationLevel: string } | null>(null);

  // Review submission is auth-gated and never indexed.
  useMeta(metaFor({ route: "notfound", base: origin() }));

  useEffect(() => {
    api.entityById(entityId!).then(setE).catch((err) => setError((err as Error).message));
  }, [entityId]);

  if (error) return <ErrorState message={error} />;
  if (!e) return <Spinner />;

  const dimDefs = dimensionsFor(e.type);
  const canWalletVerify = e.type === "token" && !!window.ethereum;

  const verifyWallet = async () => {
    setVerifErr(null);
    setVerifBusy(true);
    try {
      const accounts = (await window.ethereum!.request({ method: "eth_requestAccounts" })) as string[];
      const address = accounts[0];
      if (!address) throw new Error("No wallet account selected");
      const { nonce, message } = await api.walletNonce(e.id, address);
      const signature = (await window.ethereum!.request({
        method: "personal_sign",
        params: [message, address],
      })) as string;
      const res = await api.walletVerify(nonce, signature);
      setVerifToken(res.verificationToken);
      setVerifBadge(res.badge);
    } catch (err) {
      setVerifErr((err as { message?: string }).message ?? "Verification failed");
    } finally {
      setVerifBusy(false);
    }
  };

  const submit = async () => {
    if (rating < 1) { setError("Pick an overall rating."); return; }
    setSubmitting(true);
    try {
      const created = await api.createReview({
        entityId: e.id,
        ratingOverall: rating,
        title: title || undefined,
        body: body || undefined,
        dimensions: Object.entries(dims).map(([key, score]) => ({ key, score })),
        verificationToken: verifToken ?? undefined,
      });
      // Reviews are held for screening (SPEC §6.3), so navigating to the profile
      // would show the author nothing. Confirm in place instead.
      setSubmitted({ verificationLevel: created.verificationLevel });
    } catch (err) {
      setError((err as Error).message);
      setSubmitting(false);
    }
  };

  if (submitted) {
    const verified = submitted.verificationLevel !== "none";
    return (
      <div className="u-64479dc">
        <h1>Thanks — your review is in review</h1>
        <section className="card p-20 mb-16">
          <p>
            We run every review through automated integrity checks before publishing it.
            That usually takes a few moments; if anything needs a closer look a moderator
            reviews it. It will appear on {e.name} once the checks complete.
          </p>
          {verified ? (
            <p>
              <VerificationBadge level={submitted.verificationLevel as "wallet"} />{" "}
              Your wallet proof was accepted, so your review carries a verification badge
              and counts more toward {e.name}&rsquo;s rating.
            </p>
          ) : (
            <p className="muted">
              This review is unverified. Verified reviews carry a badge, are shown first,
              and weigh more in the rating. you can verify a wallet on your next review.
            </p>
          )}
          <div className="row gap-8 mt-12">
            <button className="btn btn-primary" onClick={() => navigate(`/${e.type}/${e.slug}`)}>
              Back to {e.name}
            </button>
            <button className="btn btn-ghost" onClick={() => navigate("/methodology")}>
              How verification works
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="u-64479dc">
      <h1>Review {e.name}</h1>

      <section className="card p-20 mb-16">
        <h3>1. Your rating</h3>
        <button className="row u-c3b7ae7" aria-label="overall rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <span key={n} onClick={() => setRating(n)} className="u-3b6a3a6">
              <Stars value={rating >= n ? 5 : 0} count={1} />
            </span>
          ))}
          <span className="muted u-5dd2a67">{rating ? `${rating}/5` : "Tap to rate"}</span>
        </button>

        <div className="grid u-8505f7b">
          {dimDefs.map((d) => (
            <div key={d.key} className="row between">
              <div>
                <div className="u-02a2d33">{d.label}</div>
                <div className="muted u-c956592">{d.hint}</div>
              </div>
              <div className="row u-d767683">
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} onClick={() => setDims((p) => ({ ...p, [d.key]: n }))} className="u-3b6a3a6">
                    <Stars value={(dims[d.key] ?? 0) >= n ? 5 : 0} count={1} />
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card p-20 mb-16">
        <h3>2. Your review</h3>
        <input className="input u-761d3ad" placeholder="Summary (e.g. Reliable and well-run)" value={title} maxLength={140} onChange={(ev) => setTitle(ev.target.value)} />
        <textarea className="input" placeholder="What was your experience? Be specific and fair." rows={5} value={body} maxLength={8000} onChange={(ev) => setBody(ev.target.value)} />
      </section>

      <section className="card p-20 mb-16">
        <h3>3. Verify (recommended)</h3>
        <p className="u-c821f2f">
          Verified reviews carry a badge and count far more toward the aggregate score. Verification
          is free and never authorizes a transaction.
        </p>
        {verifToken ? (
          <span className="badge badge-verified">✓ {verifBadge} - ready to attach</span>
        ) : canWalletVerify ? (
          <button className="btn btn-ghost" onClick={verifyWallet} disabled={verifBusy}>
            {verifBusy ? "Check your wallet…" : "Connect wallet & verify holder"}
          </button>
        ) : (
          <p className="muted u-1e8f47b">
            {e.type === "token"
              ? "Install an EVM wallet (e.g. MetaMask) to verify as a holder, or post unverified."
              : "Verify with your exchange account to post as a Verified User."}
          </p>
        )}
        {verifErr && <p className="u-b7f86d3">{verifErr}</p>}

        {e.type === "company" && (
          <a className="btn btn-ghost" href="/api/v1/verify/exchange/coinbase/start">
            Verify with your exchange
          </a>
        )}

        {/* A standing commitment, not a status update — it stays after launch. */}
        <p className="muted u-5ed22d7">
          Exchange verification uses read-only OAuth. We will <strong>never</strong> ask you for an
          exchange API key — if a site claiming to be CryptoChit does, it isn&rsquo;t us.
        </p>
      </section>

      {error && <ErrorState message={error} />}
      <div className="row gap-10">
        <button className="btn btn-primary" onClick={submit} disabled={submitting || rating < 1}>
          {submitting ? "Submitting…" : verifToken ? "Submit verified review" : "Submit review"}
        </button>
        <button className="btn btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
      </div>
    </div>
  );
}
